import {validateIssueBody, validateIssueTitle} from '@github-ui/entity-validators'
import {reportError} from '@github-ui/failbot'
import {
  commitUpdateIssueProjectsMutation,
  type ProjectPickerProject$data as Project,
} from '@github-ui/item-picker/project'
import type {RepositoryPickerRepository$data as Repository} from '@github-ui/item-picker/repository'
import {CommentBox, type CommentBoxConfig, type Subject, type ViewMode} from '@github-ui/comment-box'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {useToastContext} from '@github-ui/toast'
import {Flash, Octicon, FormControl, TextInput} from '@primer/react'
import {type FormEvent, useCallback, useEffect, useId, useImperativeHandle, useMemo, useRef, useState} from 'react'
import {useRelayEnvironment} from 'react-relay'
import type {Environment} from 'relay-runtime'

import {ERRORS, LABELS} from './constants'
import type {CreateIssueInput, createIssueMutation} from './mutations/__generated__/createIssueMutation.graphql'
import {commitCreateIssueMutation} from './mutations/create-issue-mutation'
import type {IssueCreatePayload} from '.'
import {IssueCreationKind, instanceOfIssueFormData} from './utils/model'
import {type IssueFormElementRef, IssueFormElements} from '@github-ui/issue-form'
import {AlertIcon} from '@primer/octicons-react'
import {useIssueCreateConfigContext} from './contexts/IssueCreateConfigContext'
import {ContributorFooter} from '@github-ui/contributor-footer'
import {getPotentialFormDefaultValuesFromUrl} from './utils/urls'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import {useAnalytics} from '@github-ui/use-analytics'
import {useIssueCreateDataContext} from './contexts/IssueCreateDataContext'
import {MetadataSelectors} from './MetadataSelectors'

type CreatedIssue = Exclude<Exclude<createIssueMutation['response']['createIssue'], null>['issue'], null>
export type OnCreateProps = {
  issue: CreatedIssue
  createMore: boolean
}

export type CreateIssueCallbackProps = {
  onCreateSuccess: ({issue, createMore}: OnCreateProps) => void
  onCancel: () => void
}

export type CreateIssueFormProps = {
  repository: Repository
  selectedTemplate?: IssueCreatePayload
  title: string
  setTitle: (title: string) => void
  body: string
  setBody: (body: string) => void
  clearOnCreate: () => void
  focusTitleInput?: boolean
} & CreateIssueCallbackProps

export const CreateIssueForm = ({
  repository,
  title,
  setTitle,
  body,
  setBody,
  clearOnCreate,
  onCreateSuccess,
  selectedTemplate,
  focusTitleInput,
}: CreateIssueFormProps): JSX.Element => {
  const {optionConfig} = useIssueCreateConfigContext()
  const titleInputRef = useRef<HTMLInputElement>(null)

  const [viewMode, setViewMode] = useState<ViewMode>('edit')
  const [titleValidationResult, setTitleValidationResult] = useState<string | undefined>(undefined)
  const [bodyValidationResult, setBodyValidationResult] = useState<string | undefined>(undefined)

  const {addToast} = useToastContext()
  const {
    createMore,
    createMoreCreatedPath,
    setCreateMoreCreatedPath,
    isSubmitting,
    setIsSubmitting,
    onCreateAction,
    issueFormRef,
  } = useIssueCreateConfigContext()
  const environment = useRelayEnvironment()
  const issueTypesFFEnabled = useFeatureFlag('issue_types')
  const {sendAnalyticsEvent} = useAnalytics()
  const {labels, assignees, projects, milestone, issueType} = useIssueCreateDataContext()

  const commentBoxConfig: CommentBoxConfig = {
    pasteUrlsAsPlainText: optionConfig.pasteUrlsAsPlainText,
    useMonospaceFont: optionConfig.useMonospaceFont,
  }

  useEffect(() => {
    // Focus title input on first render
    // TextInput mutates the ref so we only focus on first render
    if (!isSubmitting && focusTitleInput) titleInputRef?.current?.focus()
  }, [focusTitleInput, isSubmitting])

  const clearFields = useCallback(() => {
    setTitle('')
    setBody('')
    issueFormRef.current?.resetInputs()
  }, [issueFormRef, setBody, setTitle])

  useImperativeHandle(
    onCreateAction,
    () => ({
      onCreate: (isSubmittingArg, createMoreArg) => {
        if (isSubmittingArg) return

        let refToFocusOnError: HTMLInputElement | IssueFormElementRef | undefined = undefined

        const inputValidationResult = validateIssueTitle(title)
        setTitleValidationResult(inputValidationResult.errorMessage)
        if (!inputValidationResult.isValid && titleInputRef.current) {
          refToFocusOnError = titleInputRef.current
        }

        const isIssueForm = selectedTemplate && instanceOfIssueFormData(selectedTemplate.data)
        let issueBodyToSave = body
        if (isIssueForm && issueFormRef.current) {
          const invalidInputs = issueFormRef.current.getInvalidInputs()
          // If there are validation errors, use first input if there's no ref yet
          if (invalidInputs.length > 0) {
            refToFocusOnError = refToFocusOnError || invalidInputs[0]
          } else {
            issueBodyToSave = issueFormRef.current.markdown() ?? ''
          }
        }

        // We validate the length === 0 also for the initial state, in which the validation result will be undefined, too.
        if (
          refToFocusOnError ||
          titleValidationResult !== undefined ||
          (title ?? '').trim().length === 0 ||
          repository === undefined ||
          bodyValidationResult !== undefined
        ) {
          // If there is an input with an error, focus it
          refToFocusOnError?.focus()
          return
        }

        setIsSubmitting(true)
        let issueTemplate = undefined
        if (
          selectedTemplate &&
          (selectedTemplate.kind === IssueCreationKind.IssueTemplate ||
            selectedTemplate.kind === IssueCreationKind.IssueForm)
        ) {
          issueTemplate = selectedTemplate.name
        }

        const input: CreateIssueInput = {
          repositoryId: repository.id,
          title: title ? title.trim() : '',
          body: issueBodyToSave,
          labelIds: labels.length > 0 ? labels.map(label => label.id) : undefined,
          assigneeIds: assignees.length > 0 ? assignees.map(assignee => assignee.id) : undefined,
          milestoneId: milestone?.id,
          issueTypeId: issueTypesFFEnabled ? issueType?.id : null,
          issueTemplate,
        }

        commitCreateIssueMutation({
          environment,
          input,
          onError: (error: Error) => {
            reportError(formatError(error.message))
            setIsSubmitting(false)
            addToast({
              type: 'error',
              message: ERRORS.createIssueError,
            })
          },
          onCompleted: response => {
            setIsSubmitting(false)
            if (!response.createIssue?.issue) {
              response.createIssue?.errors.map(e => reportError(formatError(e.message)))
              addToast({
                type: 'error',
                message: ERRORS.createIssueError,
              })
              return
            }

            const newIssue = response.createIssue.issue

            sendAnalyticsEvent('analytics.click', 'ISSUE_CREATE_NEW_ISSUE_BUTTON', {
              issueId: newIssue.id,
              issueNumber: newIssue.number,
              issueNWO: `${newIssue.repository.owner.login}/${newIssue.repository.name}`,
            })

            if (projects.length > 0) {
              assignIssueToProjects(newIssue.id, projects, environment)
            }

            if (createMoreArg) {
              setViewMode('edit')
              setCreateMoreCreatedPath({...createMoreCreatedPath, number: newIssue.number})
            } else {
              clearOnCreate()
            }

            clearFields()
            onCreateSuccess({issue: newIssue, createMore: createMoreArg})
          },
        })
      },
    }),
    [
      title,
      selectedTemplate,
      body,
      issueFormRef,
      titleValidationResult,
      repository,
      bodyValidationResult,
      setIsSubmitting,
      labels,
      assignees,
      milestone?.id,
      issueTypesFFEnabled,
      issueType?.id,
      environment,
      addToast,
      sendAnalyticsEvent,
      projects,
      clearFields,
      onCreateSuccess,
      setCreateMoreCreatedPath,
      createMoreCreatedPath,
      clearOnCreate,
    ],
  )

  const handleTitleChange = (e: FormEvent<HTMLInputElement>) => {
    const inputTitle = e.currentTarget.value
    validateAndSetTitle(inputTitle)
  }

  const validateAndSetTitle = (newTitle: string) => {
    const inputValidationResult = validateIssueTitle(newTitle)
    // when title changes, check if we need to clear validation error
    // validation error are only added when user tries to create the issue
    if (inputValidationResult.isValid) {
      setTitleValidationResult(undefined)
    }
    setTitle(newTitle)
  }
  const handleBodyChange = (inputBody: string) => {
    validateAndSetBody(inputBody)
  }
  const validateAndSetBody = (newBody: string) => {
    const inputValidationResult = validateIssueBody(newBody)

    setBodyValidationResult(inputValidationResult.errorMessage)
    setBody(newBody)
  }

  const isIssueForm = selectedTemplate && instanceOfIssueFormData(selectedTemplate.data)

  const subject = useMemo<Subject | undefined>(() => {
    if (repository) {
      return {
        type: 'issue',
        repository: {
          databaseId: repository.databaseId!,
          nwo: `${repository.owner.login}/${repository.name}`,
          slashCommandsEnabled: repository.slashCommandsEnabled,
        },
      }
    }
  }, [repository])

  const validationErrorId = useId()

  return (
    <>
      <FormControl sx={{display: 'flex', flexDirection: 'column', alignItems: 'stretch'}} required>
        <FormControl.Label
          sx={{'> span > span:last-of-type': {color: 'var(--fgColor-danger, var(--color-danger-fg))'}}}
        >
          {LABELS.issueCreateTitleLabel}
        </FormControl.Label>
        <TextInput
          ref={titleInputRef}
          aria-label={LABELS.issueCreateTitleLabel}
          placeholder={LABELS.issueCreateTitlePlaceholder}
          value={title}
          onBlur={handleTitleChange}
          onChange={handleTitleChange}
          data-hpc
        />
        {titleValidationResult && (
          <FormControl.Validation variant="error">{titleValidationResult}</FormControl.Validation>
        )}
      </FormControl>
      {subject && !isIssueForm && (
        <>
          <CommentBox
            subject={subject}
            label={LABELS.issueCreateBodyLabel}
            showLabel={true}
            placeholder={LABELS.issueBodyPlaceholder}
            viewMode={viewMode}
            onChangeViewMode={setViewMode}
            value={body as SafeHTMLString}
            onSave={() => onCreateAction?.current?.onCreate(isSubmitting, createMore)}
            onChange={handleBodyChange}
            saveButtonTrailingIcon={false}
            userSettings={commentBoxConfig}
            minHeightLines={optionConfig.insidePortal ? 14 : 20}
            aria-describedby={bodyValidationResult ? validationErrorId : undefined}
          />
          {bodyValidationResult && (
            <Flash variant="danger" id={validationErrorId}>
              <Octicon icon={AlertIcon} />
              {bodyValidationResult}
            </Flash>
          )}
        </>
      )}

      {subject && isIssueForm && instanceOfIssueFormData(selectedTemplate.data) && (
        <IssueFormElements
          outputRef={issueFormRef}
          issueFormRef={selectedTemplate.data}
          subject={subject}
          commentBoxConfig={commentBoxConfig}
          sessionStorageKey={optionConfig.storageKeyPrefix}
          defaultValuesById={getPotentialFormDefaultValuesFromUrl()}
        />
      )}
      <MetadataSelectors />
      <ContributorFooter
        codeOfConductFileUrl={repository.codeOfConductFileUrl ?? undefined}
        securityPolicyUrl={repository.securityPolicyUrl ?? undefined}
        contributingFileUrl={repository.contributingFileUrl ?? undefined}
      />
    </>
  )
}

function assignIssueToProjects(issueId: string, projects: Project[], environment: Environment) {
  for (const project of projects) {
    commitUpdateIssueProjectsMutation({environment, issueId, projectId: project.id})
  }
}

function formatError(message: string) {
  return new Error(`Issue create mutation failed with error: ${message}`)
}

try{ CreateIssueForm.displayName ||= 'CreateIssueForm' } catch {}